import React, { useState } from "react";
import { Save, FolderOpen, Trash2, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { listSims, saveSim, deleteSim } from "../lib/storage";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";

export default function SavedSims({ currentState, onLoad }) {
  const { t, lang } = useI18n();
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [name, setName] = useState("");
  const [items, setItems] = useState(() => listSims());

  const refresh = () => setItems(listSims());

  const handleSave = () => {
    const n = name.trim();
    if (!n) return;
    saveSim(n, currentState);
    toast.success(lang === "it" ? "Simulazione salvata" : "Simulation saved", { description: n });
    setName("");
    setSaveOpen(false);
    refresh();
  };

  const handleLoad = (id) => {
    const item = items.find((s) => s.id === id);
    if (!item) return;
    onLoad({ startYear: item.startYear, startMonth: item.startMonth, inputs: item.inputs });
    toast.success(lang === "it" ? "Simulazione caricata" : "Simulation loaded", { description: item.name });
    setLoadOpen(false);
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    deleteSim(id);
    refresh();
  };

  const fmtDate = (ts) => new Date(ts).toLocaleDateString(lang === "it" ? "it-IT" : "en-US", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

  return (
    <>
      <Popover open={saveOpen} onOpenChange={setSaveOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="wc-btn-outline" data-testid="save-btn">
            <Save size={14} /> {lang === "it" ? "Salva" : "Save"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="wc-pop" align="start">
          <div className="wc-pop-title">{lang === "it" ? "Nome simulazione" : "Simulation name"}</div>
          <Input
            autoFocus
            placeholder={lang === "it" ? "es. Piano cliente Rossi" : "e.g. Client plan"}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            className="wc-cell-input"
            style={{ width: "100%", textAlign: "left" }}
            data-testid="save-name-input"
          />
          <div className="wc-pop-actions">
            <Button size="sm" onClick={handleSave} className="wc-btn-primary" data-testid="save-confirm">
              <Check size={14} /> {lang === "it" ? "Salva" : "Save"}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={loadOpen} onOpenChange={(o) => { setLoadOpen(o); if (o) refresh(); }}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="wc-btn-outline" data-testid="load-btn">
            <FolderOpen size={14} /> {lang === "it" ? "Carica" : "Load"}
            {items.length > 0 && <span className="wc-pop-count">{items.length}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="wc-pop wc-pop-wide" align="start">
          <div className="wc-pop-title">{lang === "it" ? "Simulazioni salvate" : "Saved simulations"}</div>
          {items.length === 0 ? (
            <div className="wc-pop-empty" data-testid="load-empty">
              {lang === "it" ? "Nessuna simulazione salvata." : "No saved simulations yet."}
            </div>
          ) : (
            <ul className="wc-pop-list" data-testid="load-list">
              {items.map((it) => (
                <li
                  key={it.id}
                  className="wc-pop-item"
                  onClick={() => handleLoad(it.id)}
                  data-testid={`load-item-${it.id}`}
                >
                  <div className="wc-pop-item-main">
                    <span className="wc-pop-item-name">{it.name}</span>
                    <span className="wc-pop-item-date">{fmtDate(it.updated_at)}</span>
                  </div>
                  <button
                    className="wc-pop-del"
                    onClick={(e) => handleDelete(it.id, e)}
                    aria-label="delete"
                    data-testid={`load-delete-${it.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
